import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getThreads, createThread, deleteThread, getLegalEntities, getOperatingEntities } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { formatDate, getStatusColor, truncateText } from '../lib/utils';
import { Plus, Target, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const ThreadsPage = () => {
  const [threads, setThreads] = useState([]);
  const [legalEntities, setLegalEntities] = useState([]);
  const [operatingEntities, setOperatingEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState({ status: '' });
  const [newThread, setNewThread] = useState({
    name: '',
    objective: '',
    status: 'Active',
    operating_entity_id: '',
    legal_entity_id: ''
  });

  const fetchData = async () => {
    try {
      const [threadsRes, legalRes, operatingRes] = await Promise.all([
        getThreads(filter.status ? { status: filter.status } : {}),
        getLegalEntities(),
        getOperatingEntities()
      ]);
      setThreads(threadsRes.data);
      setLegalEntities(legalRes.data);
      setOperatingEntities(operatingRes.data);
    } catch (error) {
      toast.error('Failed to load threads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createThread({
        ...newThread,
        operating_entity_id: newThread.operating_entity_id || null,
        legal_entity_id: newThread.legal_entity_id || null
      });
      toast.success('Thread created');
      setDialogOpen(false);
      setNewThread({ name: '', objective: '', status: 'Active', operating_entity_id: '', legal_entity_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create thread');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this thread?')) return;
    try {
      await deleteThread(id);
      toast.success('Thread deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete thread');
    }
  };

  const getEntityName = (id, entities) => {
    const entity = entities.find(e => e.id === id);
    return entity?.name || '-';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-zinc-500 font-inter">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="threads-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope text-xl font-bold text-zinc-900">Threads</h1>
          <p className="text-sm text-zinc-500 font-inter">Initiatives and conversations</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm" data-testid="create-thread-btn">
              <Plus className="w-4 h-4 mr-1" /> New Thread
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-manrope">Create Thread</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label className="font-inter text-sm">Name</Label>
                <Input
                  value={newThread.name}
                  onChange={(e) => setNewThread({ ...newThread, name: e.target.value })}
                  className="rounded-sm"
                  required
                  data-testid="thread-name-input"
                />
              </div>
              <div>
                <Label className="font-inter text-sm">Objective</Label>
                <Textarea
                  value={newThread.objective}
                  onChange={(e) => setNewThread({ ...newThread, objective: e.target.value })}
                  className="rounded-sm"
                  required
                  data-testid="thread-objective-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-inter text-sm">Operating Entity</Label>
                  <Select 
                    value={newThread.operating_entity_id || 'none'} 
                    onValueChange={(v) => setNewThread({ ...newThread, operating_entity_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {operatingEntities.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-inter text-sm">Legal Entity</Label>
                  <Select 
                    value={newThread.legal_entity_id || 'none'} 
                    onValueChange={(v) => setNewThread({ ...newThread, legal_entity_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {legalEntities.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" className="w-full rounded-sm" data-testid="submit-thread-btn">
                Create Thread
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <Select value={filter.status || 'all'} onValueChange={(v) => setFilter({ ...filter, status: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-40 rounded-sm">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Waiting">Waiting</SelectItem>
            <SelectItem value="Closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Threads List */}
      <div className="space-y-3">
        {threads.length === 0 ? (
          <Card className="border border-zinc-200 shadow-none rounded-sm">
            <CardContent className="p-8 text-center">
              <Target className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
              <p className="text-zinc-500 font-inter">No threads yet. Create one or use Intake to auto-create.</p>
            </CardContent>
          </Card>
        ) : (
          threads.map((thread) => (
            <Card key={thread.id} className="border border-zinc-200 shadow-none rounded-sm hover:border-zinc-300 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <Link to={`/threads/${thread.id}`} className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium font-inter text-zinc-900">{thread.name}</h3>
                      <Badge className={`text-xs ${getStatusColor(thread.status)}`}>{thread.status}</Badge>
                    </div>
                    <p className="text-sm text-zinc-500 font-inter mb-2">{truncateText(thread.objective, 100)}</p>
                    <div className="flex gap-4 text-xs text-zinc-400 font-mono">
                      <span>Op: {getEntityName(thread.operating_entity_id, operatingEntities)}</span>
                      <span>Legal: {getEntityName(thread.legal_entity_id, legalEntities)}</span>
                      <span>Created: {formatDate(thread.created_at)}</span>
                    </div>
                  </Link>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-zinc-400 hover:text-red-500"
                    onClick={() => handleDelete(thread.id)}
                    data-testid={`delete-thread-${thread.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default ThreadsPage;
